import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, Inject, LOCALE_ID, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { map, Observable, of, startWith, Subject, Subscription, switchMap, tap } from 'rxjs';
import { RegisterUserModel } from 'src/app/models/user';
import { AuthService } from 'src/app/services/auth.service';
import { IdentityErrorCodes } from 'src/app/utils/errorCodes';
import { compareWithField, valueInList } from 'src/app/validators/customValidators';
import * as iso_country_list from 'i18n-iso-countries';
import { MatomoTracker } from 'ngx-matomo-client';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { GeocodingSearchConstants } from 'src/app/modules/shared/constants';

@Component({
    selector: 'app-register',
    templateUrl: './register.component.html',
    styleUrls: ['./register.component.scss'],
    standalone: false
})
export class RegisterComponent implements OnInit, OnDestroy {

  @ViewChild('countryInput') 
  countryInput?: ElementRef;

  public countryList: Country[] = [];
  minPwdLength: number = 8;

  userForm = this.fb.group({
      userName: ['', [Validators.required, Validators.email]],
      phoneNumber: [null], //send null instead of empty space for optional fields
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      password: ['', [Validators.required, Validators.minLength(this.minPwdLength)]],
      confirmPassword: ['', [Validators.required, compareWithField('password')]],
      age18: [false, Validators.requiredTrue],
      acceptTOS: [false, Validators.requiredTrue],
      newsletter: [false],
      countryCode: ['', Validators.required],
      altcha: ['', Validators.required]
    }
  );

  clickedSubmit: boolean = false;
  sendingRequest: boolean = false;
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;
  isOAuthUser: boolean = false;
  subscribedToNews: boolean = false;

  subscriberForm = this.fb.group({
    subscriberEmail: ['', [Validators.email, Validators.minLength(1)]]
  });

  private readonly errorMessage = $localize`Sorry, an error has happened`;
  private userSub$ : Subscription;
  private onCountryInputKeyUpSubject$ = new Subject<KeyboardEvent>();
  public filteredCountries$: Observable<Country[]> = of([]);

  constructor(public authService: AuthService,
    private router: Router,
    private fb: FormBuilder,
    private toastrService: ToastrService,
    private renderer: Renderer2,
    private matomoTracker: MatomoTracker,
    @Inject(LOCALE_ID) public locale: string) {
      const linkElement = this.renderer.createElement('link');
      this.renderer.setAttribute(linkElement, 'rel', 'stylesheet');
      this.renderer.setAttribute(linkElement, 'href', 'https://cdn.jsdelivr.net/gh/lipis/flag-icons@7.2.3/css/flag-icons.min.css');
      this.renderer.setAttribute(linkElement, 'defer', 'true');
      this.renderer.appendChild(document.head, linkElement);

      this.userSub$ = this.authService.user$.subscribe(user => {
        if (user.user !== undefined && user.user.loginProvider != "HorseHop") {
          this.isOAuthUser = true;
          this.userForm.patchValue({
            firstName: user.user!.firstName,
            lastName: user.user!.lastName,
            userName: user.user!.email,
          });

          // No need to validate password fields for OAuth users
          this.userForm.controls['password'].clearValidators();
          this.userForm.controls['confirmPassword'].clearValidators();
          this.userForm.controls['altcha'].clearValidators();
          this.userForm.controls['password'].updateValueAndValidity();
          this.userForm.controls['confirmPassword'].updateValueAndValidity();
          this.userForm.controls['altcha'].updateValueAndValidity();
          this.userForm.controls['password'].setValue(null);
          this.userForm.controls['confirmPassword'].setValue(null);
          this.userForm.controls['altcha'].setValue(null);
        }
      });
  }

  async ngOnInit(): Promise<void> {
    //These files are added to wwwroot on the server side
    const response = await fetch(`/i18n-iso-countries/langs/${this.locale.split('-')[0]}.json`);
    const locale = await response.json();
    iso_country_list.registerLocale(locale);

    let countryContainer = iso_country_list.getNames(this.locale.split('-')[0]);
    this.countryList = Object.entries(countryContainer).map(([key, value]) => {
      return {
        label: value,
        value: key
      };
    });

    // Set country list validation after list is initialized
    this.userForm.controls['countryCode'].setValidators([
      Validators.required,
      valueInList(this.countryList.map(c => c.value))
    ]);

    this.filteredCountries$ = this.onCountryInputKeyUpSubject$.pipe(
      map(event => (event.target as HTMLInputElement)?.value ?? ''), //extract value from event
      tap(this.clearControlIfEmptyValue.bind(this, 'countryCode')),
      startWith(''),
      map(value => this._filterCountries(value))
    );
  }

  ngOnDestroy(): void {
    this.userSub$.unsubscribe();
    this.onCountryInputKeyUpSubject$.complete();
  }

  getCountryName(countryCode: string): string {
    return iso_country_list.getName(countryCode, this.locale.split('-')[0])!;
  }

  selectCountry(event: MatAutocompleteSelectedEvent): void {
    this.userForm.controls['countryCode'].setValue(event.option.value);
  }

  public onCountryInputKeyUp(event : KeyboardEvent) {
    if(GeocodingSearchConstants.ignoreSearchKeys.indexOf(event.key) > -1) {
      return;
    }
    this.onCountryInputKeyUpSubject$.next(event);
  }

  private _filterCountries(value: string | null): Country[] {
    const filterValue = value ? value.toLowerCase() : '';

    return this.countryList.filter(country => country.label.toLowerCase().includes(filterValue));
  }

  private clearControlIfEmptyValue(controlName: string, value : string) {
    if(value == null || value.length == 0) {
      this.userForm.patchValue({
        [controlName]: null
      });
    }
  }

  register() {
    this.clickedSubmit = true;

    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }

    let registerModel = new RegisterUserModel();

    registerModel.userName = this.userForm.controls['userName'].value!;
    registerModel.password = this.userForm.controls['password'].value!;
    registerModel.firstName = this.userForm.controls['firstName'].value!;
    registerModel.lastName = this.userForm.controls['lastName'].value!;
    registerModel.phoneNumber = this.userForm.controls['phoneNumber'].value!;
    registerModel.subscribe = this.userForm.controls['newsletter'].value!;
    registerModel.olderThan18Years = this.userForm.controls['age18'].value!;
    registerModel.acceptTerms = this.userForm.controls['acceptTOS'].value!;
    registerModel.countryCode = this.userForm.controls['countryCode'].value!;
    registerModel.altcha = this.userForm.controls['altcha'].value!;

    this.sendingRequest = true;

    var req$ = this.isOAuthUser ? this.authService.registerOAuthUser(registerModel) : this.authService.register(registerModel);
    req$.subscribe({
        next: (user) => {
          let newrelic = (window as any).newrelic;
          if (newrelic && newrelic.setUserId)  {
            newrelic.setUserId(user.id);
            newrelic.recordReplay();
          }
          this.matomoTracker.setUserId(user.id);
          this.matomoTracker.trackEvent('User', 'Sign Up', this.isOAuthUser ? 'Google Sign Up' : 'Native Sign Up');
          this.router.navigateByUrl('/home');
          this.sendingRequest = false;
        },
        error: (errResp: HttpErrorResponse) => {
          this.sendingRequest = false;
          const errorCodes = errResp.error as Array<string>;
          if (errorCodes != null && (errorCodes.indexOf(IdentityErrorCodes.DuplicateUserName) > -1 ||
            errorCodes.indexOf(IdentityErrorCodes.DuplicateEmail) > -1)) {

            this.toastrService.info(
              $localize`The email ${registerModel.userName} is already registered`, undefined,
               { timeOut: 3000 });
          }
          else {
            this.toastrService.error(this.errorMessage, undefined, { timeOut: 5000 });
          }
        }
      });
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  public subscribe() {
    var subscriberEmail = this.subscriberForm.controls['subscriberEmail'].value!

    if (this.subscriberForm.valid && subscriberEmail.length > 0) {
      this.authService.subscribe(subscriberEmail).subscribe(_ => {
        this.subscribedToNews = true;
      });
    }
  }
}

export class Country {
  label!: string;
  value!: string;
}
